import { components, paths } from "@/api-schema";
import {
  PathsV1VehicleSearchPostParametersQueryExtend,
  PathsV1VehicleSearchPostParametersQueryOrderByDirection,
  PathsV1VehicleSearchPostParametersQueryOrderByField,
  PathsV1VehicleSearchPostParametersQuerySearchField,
  DataExportType,
} from "@/api-schema-enums";
import { useAbility } from "@/casl-provider";
import { DEFAULT_PAGE_SIZE } from "@/common/api/_defaults";
import { useVehicleSearchQuery } from "@/common/api/vehicle/use-vehicle-search.query";
import { buildVehicleSearchBody } from "@/common/helpers/build-vehicle-search-body";
import { useVehicleActions } from "@/common/hooks/vehicle/use-vehicle-actions";
import { BranchFilter } from "@/components/filter/predefined/branch-filter";
import { BrandFilter } from "@/components/filter/predefined/brand-filter";
import { ModelFamilyFilter } from "@/components/filter/predefined/model-family-filter";
import { ModelGenerationFilter } from "@/components/filter/predefined/model-generation-filter";
import { VehicleBlockedFilter } from "@/components/filter/predefined/vehicle-blocked-filter";
import { VehicleFuelTypeFilter } from "@/components/filter/predefined/vehicle-fuel-type-filter";
import { VehicleKindFilter } from "@/components/filter/predefined/vehicle-kind-filter";
import {
  VehicleMileageRangeFilter,
  VehiclePowerKwRangeFilter,
  VehiclePowerPsRangeFilter,
} from "@/components/filter/predefined/vehicle-range-filters";
import { VehicleRegistrationDateRangeFilter } from "@/components/filter/predefined/vehicle-registration-date-range-filter";
import { VehicleStatusFilter } from "@/components/filter/predefined/vehicle-status-filter";
import { VehicleTypeFilter } from "@/components/filter/predefined/vehicle-type-filter";
import { VehicleSubComponent } from "@/modules/masterdata/vehicle/_components/vehicle-sub-component";
import { userConfigStore } from "@/store/user-config-store";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { RowWithIdentifier } from "../_helpers/row-identifier";
import { DataTableRow, TableFeatures } from "../types";
import { createBasePredefinedTable } from "./_components/base-predefined-table";
import {
  BasePredefinedTableProps,
  DataFetchHooks,
  PredefinedTableConfig,
} from "./_components/base-predefined-table.types";
import { vehicleTableDefaultColumns } from "./vehicle-table-columns";

type Vehicle = components["schemas"]["Vehicle"] & RowWithIdentifier;
type VehicleSorting = NonNullable<paths["/v1/vehicle/search"]["post"]["parameters"]["query"]["orderBy"]>;
type VehicleQueryParams = paths["/v1/vehicle/search"]["post"]["parameters"]["query"];
type VehicleRequestBody = paths["/v1/vehicle/search"]["post"]["requestBody"]["content"]["application/json"];
type VehicleExtend = NonNullable<paths["/v1/vehicle/search"]["post"]["parameters"]["query"]["extend"]>;

const DEFAULT_EXTEND: VehicleExtend = [
  PathsV1VehicleSearchPostParametersQueryExtend.ModelGeneration,
  PathsV1VehicleSearchPostParametersQueryExtend.ModelGeneration_ModelFamily,
  PathsV1VehicleSearchPostParametersQueryExtend.ModelGeneration_ModelFamily_Brand,
  PathsV1VehicleSearchPostParametersQueryExtend.VehicleBlock,
  PathsV1VehicleSearchPostParametersQueryExtend.RelocationOrders,
  PathsV1VehicleSearchPostParametersQueryExtend.OwningBranch,
  PathsV1VehicleSearchPostParametersQueryExtend.OwningBranch_Company,
  PathsV1VehicleSearchPostParametersQueryExtend.OwningBranch_Brands,
  PathsV1VehicleSearchPostParametersQueryExtend.OwningBranch_Address,
  PathsV1VehicleSearchPostParametersQueryExtend.CurrentBranch,
  PathsV1VehicleSearchPostParametersQueryExtend.CurrentBranch_Company,
  PathsV1VehicleSearchPostParametersQueryExtend.CurrentBranch_Brands,
  PathsV1VehicleSearchPostParametersQueryExtend.CurrentBranch_Address,
] as const;

const VEHICLE_SEARCH_FIELDS = Object.values(PathsV1VehicleSearchPostParametersQuerySearchField);

const DEFAULT_COLUMNS = [
  vehicleTableDefaultColumns.mobile.columnMobileComplete,
  vehicleTableDefaultColumns.desktopAndTablet.columnBrandModelDescription,
  vehicleTableDefaultColumns.desktopAndTablet.columnVinColorTransportStatus,
  vehicleTableDefaultColumns.widescreen.columnBrand,
  vehicleTableDefaultColumns.widescreen.columnModelDescription,
  vehicleTableDefaultColumns.widescreen.columnVin,
  vehicleTableDefaultColumns.widescreen.columnColor,
  vehicleTableDefaultColumns.widescreen.columnRegistrationDate,
  vehicleTableDefaultColumns.widescreen.columnReimport,
  vehicleTableDefaultColumns.widescreenDesktopAndTablet.columnOwningBranch,
  vehicleTableDefaultColumns.widescreenDesktopAndTablet.columnCurrentBranch,
  vehicleTableDefaultColumns.widescreen.columnTransportStatus,
];

const VEHICLE_TABLE_CONFIG: PredefinedTableConfig<VehicleSorting> = {
  defaultPageSize: DEFAULT_PAGE_SIZE,
  defaultSorting: [
    {
      direction: PathsV1VehicleSearchPostParametersQueryOrderByDirection.Desc,
      field: PathsV1VehicleSearchPostParametersQueryOrderByField.CreatedAt,
    },
  ] as const,
  defaultTableConfig: {
    searchPlaceholder: "common:SearchVehicle",
    features: {
      multiSelection: true,
      pagination: true,
      search: true,
      expansion: true,
    } as TableFeatures,
  } as const,
  defaultRowActions: {
    showEdit: false,
    showDelete: false,
    showView: false,
    viewLabel: "common:ViewVehicle",
  } as const,
  sortingMapping: {
    brand: ["modelGeneration.modelFamily.brand"],
    brandModelDescription: ["modelGeneration.modelFamily.brand", "modelDescription"],
  } as const,
  exportType: DataExportType.Vehicle,
} as const;

const VEHICLE_TABLE_QUERY_HOOKS: DataFetchHooks<Vehicle, VehicleQueryParams, VehicleRequestBody> = {
  useSearchQuery: useVehicleSearchQuery,
} as const;

export interface VehicleTableProps extends BasePredefinedTableProps<
  Vehicle,
  VehicleSorting,
  VehicleQueryParams,
  VehicleRequestBody
> {
  extend?: VehicleExtend;
  showBlockedFilter?: boolean;
  showVehicleValuationAction?: boolean;
}

const BaseVehicleTable = createBasePredefinedTable({
  config: VEHICLE_TABLE_CONFIG,
  queryHooks: VEHICLE_TABLE_QUERY_HOOKS,
  defaultColumns: DEFAULT_COLUMNS,
});

const DEFAULT_FILTER_VALUES: Record<string, string[]> = {
  branches: [],
  brands: [],
  modelFamilies: [],
  modelGenerations: [],
  blockedVehicles: [],
  vehicleStatus: [],
  fuelType: [],
  vehicleType: [],
  vehicleKind: [],
  registrationDateRange: [],
  mileageRange: [],
  powerKwRange: [],
  powerPsRange: [],
};

export function VehicleTable(props: VehicleTableProps) {
  const {
    name = "vehicle-table",
    extend = DEFAULT_EXTEND,
    body: externalBody,
    query,
    showBlockedFilter = true,
    showVehicleValuationAction = false,
    ...restProps
  } = props;
  const { t } = useTranslation(["common", "masterdata", "fit", "fab"]);

  const ability = useAbility();
  const dateFormat = userConfigStore((state) => state.dateFormat);
  const { getActionsForVehicle, dialogs } = useVehicleActions({
    showVehicleValuationAction,
  });

  const [filterValues, setFilterValues] = useState<Record<string, string[]>>(DEFAULT_FILTER_VALUES);

  const filters = useMemo(
    () => [
      new BranchFilter().setLabelOverrides({
        labelSingular: t("common:Location"),
        labelPlural: t("fit:Locations"),
        placeholder: t("fit:SelectLocations"),
        searchPlaceholder: t("fit:SearchLocations"),
        emptyText: t("fit:NoLocationsFound"),
      }),
      new BrandFilter(),
      new ModelFamilyFilter({ body: { brand: filterValues.brands || [] } }),
      new ModelGenerationFilter({
        body: {
          brand: filterValues.brands || [],
          modelFamily: filterValues.modelFamilies || [],
        },
      }),
      new VehicleFuelTypeFilter(),
      new VehicleStatusFilter({ showAdminStatuses: ability.can("manage", "VehicleStatus") }),
      new VehicleKindFilter(),
      new VehicleTypeFilter(),
      new VehicleRegistrationDateRangeFilter({ id: "registrationDateRange" }),
      new VehicleMileageRangeFilter({ id: "mileageRange" }),
      new VehiclePowerKwRangeFilter({ id: "powerKwRange" }),
      new VehiclePowerPsRangeFilter({ id: "powerPsRange" }),
      ...(showBlockedFilter ? [new VehicleBlockedFilter()] : []),
    ],
    [ability, filterValues.brands, filterValues.modelFamilies, showBlockedFilter, t],
  );

  const handleFilterChange = useCallback((filterId: string, values: string[]) => {
    setFilterValues((prev) => {
      const newValues = {
        ...prev,
        [filterId]: values,
      };

      if (filterId === "brands") {
        newValues.modelFamilies = [];
        newValues.modelGenerations = [];
      }

      if (filterId === "modelFamilies") {
        newValues.modelGenerations = [];
      }

      return newValues;
    });
  }, []);

  const vehicleBody: VehicleRequestBody = useMemo(
    () =>
      buildVehicleSearchBody({
        externalBody,
        filterValues,
        dateFormat,
      }),
    [externalBody, filterValues, dateFormat],
  );

  const vehicleQuery: VehicleQueryParams = useMemo(
    () => ({
      ...query,
      searchField: query?.searchField ?? VEHICLE_SEARCH_FIELDS,
    }),
    [query],
  );

  const customRowActions = useCallback(
    (row: DataTableRow<Vehicle>) =>
      getActionsForVehicle(row.original).map(({ label, icon: Icon, onClick }) => ({
        label,
        icon: Icon ? <Icon className="h-4 w-4" /> : undefined,
        onClick,
      })),
    [getActionsForVehicle],
  );

  return (
    <>
      <BaseVehicleTable
        searchPlaceholder={t("common:SearchVehicle")}
        name={name}
        query={vehicleQuery}
        body={vehicleBody}
        filters={filters}
        filterValues={filterValues}
        filterContainerClassName="w-96"
        onFilterChange={handleFilterChange}
        customRowActions={customRowActions}
        extend={extend}
        subComponent={VehicleSubComponent}
        {...restProps}
      />

      {dialogs}
    </>
  );
}
